import { Pressable, StyleSheet, View } from 'react-native';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import type { StudyPlanDayViewModel } from '../../types/studyPlan';
import { AppText } from '../ui/AppText';

type WeekCalendarStripProps = {
  days: StudyPlanDayViewModel[];
  selectedDateId: string;
  onSelectDate: (id: string) => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onOpenMonthPicker: () => void;
};

export function WeekCalendarStrip({
  days,
  selectedDateId,
  onSelectDate,
  onPreviousWeek,
  onNextWeek,
  onOpenMonthPicker,
}: WeekCalendarStripProps) {
  return (
    <View style={styles.wrapper}>
      <View style={styles.header}>
        <Pressable accessibilityRole="button" onPress={onPreviousWeek} style={styles.navButton}>
          <ChevronLeft size={18} color={tokens.colors.text} />
        </Pressable>
        <Pressable accessibilityRole="button" onPress={onOpenMonthPicker} style={styles.monthButton}>
          <CalendarDays size={16} color={tokens.colors.primary} />
          <AppText variant="body" tone="primary" weight="bold">
            本周
          </AppText>
        </Pressable>
        <Pressable accessibilityRole="button" onPress={onNextWeek} style={styles.navButton}>
          <ChevronRight size={18} color={tokens.colors.text} />
        </Pressable>
      </View>
      <View style={styles.daysRow}>
        {days.map((day) => {
          const selected = day.id === selectedDateId;
          return (
            <Pressable
              key={day.id}
              accessibilityRole="button"
              onPress={() => onSelectDate(day.id)}
              style={[styles.dayItem, selected ? styles.dayItemSelected : null]}
            >
              <AppText variant="caption" tone={selected ? 'default' : 'muted'} weight="semibold">
                {day.weekdayLabel}
              </AppText>
              <AppText
                variant="body"
                weight="bold"
                style={selected ? styles.selectedDayText : null}
                tone={selected ? 'default' : 'default'}
              >
                {day.dayLabel}
              </AppText>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    gap: tokens.spacing.md,
  },
  header: {
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  monthButton: {
    minHeight: 36,
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  daysRow: {
    flexDirection: 'row',
    gap: tokens.spacing.xs,
  },
  dayItem: {
    flex: 1,
    minHeight: 58,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    backgroundColor: tokens.colors.surface,
  },
  dayItemSelected: {
    backgroundColor: tokens.colors.primary,
  },
  selectedDayText: {
    color: tokens.colors.white,
  },
});
